/**
 * Category Manager Component
 * Manage service categories
 */

import { useState } from 'react';
import { XMarkIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { adminAPI } from '../../services/api';

export default function CategoryManager({ categories, onUpdate, onClose }) {
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.trim()) {
      setError('Category name is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await adminAPI.categories.create({ name: newCategory.trim() });
      setNewCategory('');
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create category');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCategory = async (id, name) => {
    if (!name.trim()) {
      setError('Category name is required');
      return;
    }

    setLoading(true);
    setError('');
    try {
      await adminAPI.categories.update(id, { name: name.trim() });
      setEditingCategory(null);
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update category');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Are you sure you want to delete this category? Services in this category will become uncategorized.')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      await adminAPI.categories.delete(id);
      onUpdate();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete category');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-900">
            Manage Categories
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-lg mb-4 text-sm">
            {error}
          </div>
        )}

        {/* Add new category form */}
        <form onSubmit={handleAddCategory} className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Add New Category
          </label>
          <div className="flex space-x-2">
            <input
              type="text"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              placeholder="Category name"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              disabled={loading}
            />
            <button
              type="submit"
              disabled={loading || !newCategory.trim()}
              className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 flex items-center"
            >
              <PlusIcon className="w-5 h-5" />
            </button>
          </div>
        </form>

        {/* Existing categories list */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Existing Categories
          </label>
          {categories.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-8">
              No categories yet. Add one above to get started.
            </p>
          ) : (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {categories.map(category => (
                <CategoryItem
                  key={category.id}
                  category={category}
                  isEditing={editingCategory?.id === category.id}
                  onEdit={() => setEditingCategory(category)}
                  onSave={(name) => handleUpdateCategory(category.id, name)}
                  onCancel={() => setEditingCategory(null)}
                  onDelete={() => handleDeleteCategory(category.id)}
                  disabled={loading}
                />
              ))}
            </div>
          )}
        </div>

        <div className="flex justify-end pt-4 mt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryItem({ category, isEditing, onEdit, onSave, onCancel, onDelete, disabled }) {
  const [editName, setEditName] = useState(category.name);

  const handleSave = () => {
    onSave(editName);
  };

  const handleCancel = () => {
    setEditName(category.name);
    onCancel();
  };

  if (isEditing) {
    return (
      <div className="flex items-center space-x-2 p-2 bg-gray-50 rounded-lg">
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          className="flex-1 px-3 py-1.5 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          autoFocus
          onKeyPress={(e) => {
            if (e.key === 'Enter') handleSave();
            if (e.key === 'Escape') handleCancel();
          }}
        />
        <button
          onClick={handleSave}
          disabled={disabled || !editName.trim()}
          className="px-3 py-1.5 text-sm bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
        >
          Save
        </button>
        <button
          onClick={handleCancel}
          disabled={disabled}
          className="px-3 py-1.5 text-sm bg-white border border-gray-300 text-gray-700 rounded hover:bg-gray-50"
        >
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
      <span className="text-sm font-medium text-gray-900">{category.name}</span>
      <div className="flex space-x-1">
        <button
          onClick={onEdit}
          disabled={disabled}
          className="p-1.5 text-gray-400 hover:text-gray-600 rounded hover:bg-gray-200"
          title="Edit category"
        >
          <PencilIcon className="w-4 h-4" />
        </button>
        <button
          onClick={onDelete}
          disabled={disabled}
          className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-gray-200"
          title="Delete category"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
